import { User } from '../types';
import { api, API_BASE } from './api';
import { signInWithGoogle, firebaseSignOut, getIdToken, checkRedirectResult, FirebaseUser } from './firebase';

const CURRENT_USER_KEY = 'absorbit_current_user';
const FIREBASE_TOKEN_KEY = 'absorbit_firebase_token';

export const hashPassword = async (password: string): Promise<string> => {
  const msgBuffer = new TextEncoder().encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

export const registerUser = async (
  email: string,
  password: string,
  profileData: Partial<User> = {}
): Promise<User> => {
  const user = await api.auth.register(email, password, profileData);
  if (!user || !user.id) {
    throw new Error('Registration failed - no user returned');
  }
  setCurrentUser(user);
  return user;
};

export const loginUser = async (email: string, password: string): Promise<User> => {
  const user = await api.auth.login(email, password);
  if (!user || !user.id) {
    throw new Error('Login failed - no user returned');
  }
  setCurrentUser(user);
  return user;
};

export const updateUserProfile = async (user: User, password?: string): Promise<User> => {
  const updatedUser = await api.auth.updateProfile(user, password);
  setCurrentUser(updatedUser);
  return updatedUser;
};

// ============== GOOGLE OAUTH WITH FIREBASE ==============

/**
 * Sign in with Google using Firebase Authentication
 */
export const googleLogin = async (): Promise<User> => {
  try {
    const firebaseUser = await signInWithGoogle();
    const idToken = await firebaseUser.getIdToken();
    if (idToken) {
      localStorage.setItem(FIREBASE_TOKEN_KEY, idToken);
    }
    const user = await processFirebaseUser(firebaseUser);
    return user;
  } catch (error: any) {
    console.error('Google login error:', error);
    if (error.message === 'Redirecting to Google sign-in...') {
      throw error;
    }
    throw new Error(error.message || 'Google sign-in failed');
  }
};

/**
 * Process Firebase user and sync with our backend
 */
const processFirebaseUser = async (firebaseUser: FirebaseUser): Promise<User> => {
  const idToken = await firebaseUser.getIdToken();

  const response = await fetch(`${API_BASE}/auth/google`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      idToken,
      email: firebaseUser.email,
      name: firebaseUser.displayName,
      picture: firebaseUser.photoURL,
      googleId: firebaseUser.uid,
    }),
  });

  // Get raw text first to handle empty responses
  const text = await response.text();

  if (!response.ok) {
    let errorMessage = 'Failed to authenticate with Google';
    try {
      const error = JSON.parse(text);
      errorMessage = error.error || errorMessage;
    } catch {
      console.error('Backend error response:', text);
    }
    throw new Error(errorMessage);
  }

  if (!text) {
    throw new Error('Empty response from server');
  }

  const data = JSON.parse(text);
  const user: User & { needsOnboarding?: boolean } = {
    ...data.user,
    picture: firebaseUser.photoURL,
    // Mark as needing onboarding if no subjects/level set (new Google user)
    needsOnboarding: !data.user.level || !data.user.subjects || data.user.subjects.length === 0,
  };

  setCurrentUser(user);
  return user;
};

/**
 * Check for redirect result on app load (for when popup is blocked)
 */
export const handleGoogleRedirect = async (): Promise<User | null> => {
  try {
    const firebaseUser = await checkRedirectResult();
    if (firebaseUser) {
      const idToken = await firebaseUser.getIdToken();
      if (idToken) {
        localStorage.setItem(FIREBASE_TOKEN_KEY, idToken);
      }
      return await processFirebaseUser(firebaseUser);
    }
    return null;
  } catch (error) {
    console.error('Redirect handling error:', error);
    return null;
  }
};

/**
 * Sign out from both Firebase and local storage
 */
export const logout = async (): Promise<void> => {
  try {
    await firebaseSignOut();
  } catch (error) {
    console.error('Firebase sign out error:', error);
  }
  setCurrentUser(null);
};

// Legacy compatibility exports
export const initiateGoogleLogin = googleLogin;
export const processGoogleCallback = async (_sessionId: string): Promise<User> => {
  throw new Error('Legacy callback not supported - use Firebase Google sign-in');
};
export const getGoogleSessionIdFromUrl = (): string | null => null;
export const setupOAuthDeepLinkListener = (_onSessionId: (sessionId: string) => void): (() => void) => () => {};

export const getCurrentUser = (): User | null => {
  try {
    const stored = localStorage.getItem(CURRENT_USER_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const setCurrentUser = (user: User | null) => {
  if (user) {
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(CURRENT_USER_KEY);
    localStorage.removeItem(FIREBASE_TOKEN_KEY);
  }
};

export const getUserByEmail = async (email: string): Promise<User | null> => {
  try {
    return await api.auth.getUserByEmail(email);
  } catch {
    return null;
  }
};

export const getFirebaseToken = (): string | null => {
  return localStorage.getItem(FIREBASE_TOKEN_KEY);
};

export const refreshFirebaseToken = async (): Promise<string | null> => {
  const token = await getIdToken();
  if (token) {
    localStorage.setItem(FIREBASE_TOKEN_KEY, token);
  }
  return token;
};
